import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gift, Trophy, Loader2, Check, Ticket } from 'lucide-react';
import { raffleAPI } from '@/lib/api';

/**
 * Enter a single lead into one of the active raffles assigned to that
 * lead's agent. On enroll the lead gets the bilingual raffle SMS.
 */
export default function EnrollRaffleDialog({ open, onOpenChange, leadId, leadName, onDone }) {
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [ticketNumber, setTicketNumber] = useState('');

  useEffect(() => {
    if (!open || !leadId) return;
    setTicketNumber('');
    setLoading(true);
    raffleAPI.forLead(leadId)
      .then((r) => setRaffles(r.data.raffles || []))
      .catch(() => toast.error('Failed to load raffles'))
      .finally(() => setLoading(false));
  }, [open, leadId]);

  const enroll = async (raffle) => {
    setEnrolling(raffle.raffle_id);
    try {
      const r = await raffleAPI.enroll(raffle.raffle_id, leadId, ticketNumber.trim() || undefined);
      toast.success(r.data?.sms_sent ? 'Entered — confirmation SMS sent' : 'Lead entered in raffle');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrolling(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="enroll-raffle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift size={16} className="text-amber-600" /> Enter {leadName || 'lead'} in a raffle
          </DialogTitle>
          <DialogDescription>Pick an active raffle for this lead's agent. The lead gets a bilingual confirmation SMS.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : raffles.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="enroll-raffle-empty">
            No active raffles available for this lead's agent. Create one in the Raffles tab first.
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="raffle-ticket" className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                <Ticket size={13} className="text-slate-400" /> Ticket number <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="raffle-ticket"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                placeholder="e.g. 0042"
                className="mt-1 h-9"
                data-testid="enroll-ticket-input"
              />
              <p className="mt-1 text-[11px] text-slate-400">If set, it's included in the confirmation SMS.</p>
            </div>
            {raffles.map((r) => (
              <div key={r.raffle_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`enroll-raffle-${r.raffle_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{r.name}</p>
                  <p className="text-xs text-amber-700 flex items-center gap-1"><Trophy size={11} /> {r.prize}</p>
                </div>
                {r.already_entered ? (
                  <span className="text-xs text-emerald-600 flex items-center gap-1 shrink-0"><Check size={13} /> Entered</span>
                ) : (
                  <Button size="sm" onClick={() => enroll(r)} disabled={enrolling === r.raffle_id} className="shrink-0" data-testid={`enroll-btn-${r.raffle_id}`}>
                    {enrolling === r.raffle_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enter'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
